import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  BRAND_COLORS,
  WEBSITE_COLORS,
  LOGOS,
  LOGO_DIMENSIONS,
} from "../lib/utils";

// Navigation data structure
interface NavigationItem {
  id: string;
  label: string;
  to: string;
  icon: string;
  hoverKey: string;
}

interface SubNavigationItem {
  id: string;
  label: string;
  to: string;
  external?: boolean;
}

const navigationItems: NavigationItem[] = [
  {
    id: "home",
    label: "Home",
    to: "/",
    icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
    hoverKey: "home",
  },
  {
    id: "safety",
    label: "Safety",
    to: "/safety",
    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
    hoverKey: "safety",
  },
  {
    id: "pricing",
    label: "Pricing",
    to: "/pricing",
    icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    hoverKey: "pricing",
  },
  {
    id: "about",
    label: "About Us",
    to: "/about",
    icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    hoverKey: "about",
  },
  {
    id: "news",
    label: "News",
    to: "/news",
    icon: "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z",
    hoverKey: "news",
  },
  {
    id: "blog",
    label: "Blog",
    to: "/blog",
    icon: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z",
    hoverKey: "blog",
  },
  {
    id: "contact",
    label: "Contact Us",
    to: "/contact",
    icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
    hoverKey: "contact",
  },
  {
    id: "careers",
    label: "Careers",
    to: "/careers",
    icon: "M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.769-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.769-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z",
    hoverKey: "careers",
  },
];

const solutionsSubItems: SubNavigationItem[] = [
  { id: "all-solutions", label: "All Solutions", to: "/solutions" },
  {
    id: "chiropractors",
    label: "Chiropractors",
    to: "/solutions/chiropractors",
  },
  {
    id: "fitness",
    label: "Fitness & Nutrition Professionals",
    to: "/solutions/fitness",
  },
  {
    id: "supplements",
    label: "Supplement & Nutrition Stores",
    to: "/solutions/supplements",
  },
  { id: "clinics", label: "Medical Clinics", to: "/solutions/clinics" },
  {
    id: "entrepreneurs",
    label: "Entrepreneurs",
    to: "/solutions/entrepreneurs",
  },
];

const aboutUsSubItems: SubNavigationItem[] = [
  { id: "about", label: "About Us", to: "/about" },
  { id: "news", label: "News", to: "/news" },
  {
    id: "videos",
    label: "Videos",
    to: "https://www.youtube.com/@Axentra-Health",
    external: true,
  },
  { id: "terms", label: "Terms and Conditions", to: "/terms-and-conditions" },
];

// Reusable Sub Navigation Link Component
interface SubNavigationLinkProps {
  item: SubNavigationItem;
}

const SubNavigationLink: React.FC<SubNavigationLinkProps> = ({ item }) => {
  const className =
    "flex items-center px-6 py-2 hover:bg-[#cce5ff] rounded-lg transition-all duration-200 lg:px-4 lg:py-2 text-base lg:text-sm";

  if (item.external) {
    return (
      <a
        href={item.to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        role="menuitem"
        onClick={(e) => e.stopPropagation()}
      >
        {item.label}
      </a>
    );
  }

  return (
    <Link
      to={item.to}
      state={{ usr: { keepMenuOpen: true } }}
      className={className}
      onClick={(e) => e.stopPropagation()}
      role="menuitem"
    >
      {item.label}
    </Link>
  );
};
export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [iconOpen, setIconOpen] = useState(false);
  // Separate state for desktop and mobile dropdowns
  const [desktopSolutionsOpen, setDesktopSolutionsOpen] = useState(false);
  const [desktopAboutUsOpen, setDesktopAboutUsOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileAboutUsOpen, setMobileAboutUsOpen] = useState(false);

  // Refs for focus management
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);
  const firstMenuItemRef = useRef<HTMLAnchorElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const solutionsDesktopRef = useRef<HTMLDivElement>(null);
  const aboutUsDesktopRef = useRef<HTMLDivElement>(null);

  const toggleMobileMenu = () => {
    if (!mobileMenuOpen) {
      setMobileMenuOpen(true);
      setIconOpen(true);
      // Trigger animation after menu is mounted
      setTimeout(() => {
        setIsAnimating(true);
        // Focus first menu item after animation starts
        setTimeout(() => {
          firstMenuItemRef.current?.focus();
        }, 50);
      }, 10);
    } else {
      // Start closing animation
      setIsAnimating(false);
      setIconOpen(false);
      // Hide menu after animation completes and return focus to hamburger button
      setTimeout(() => {
        setMobileMenuOpen(false);
        hamburgerButtonRef.current?.focus();
      }, 300);
    }
  };

  const closeMenu = () => {
    // Start closing animation
    setIsAnimating(false);
    setIconOpen(false);
    // Hide menu after animation completes and return focus to hamburger button
    setTimeout(() => {
      setMobileMenuOpen(false);
      hamburgerButtonRef.current?.focus();
    }, 300);
  };

  const closeMenuAndDropdowns = () => {
    closeMenu();
    setDesktopSolutionsOpen(false);
    setDesktopAboutUsOpen(false);
    setMobileSolutionsOpen(false);
    setMobileAboutUsOpen(false);
  };

  // Desktop dropdown toggles
  const toggleDesktopSolutions = () => {
    setDesktopSolutionsOpen(!desktopSolutionsOpen);
    setDesktopAboutUsOpen(false); // Close other desktop dropdown
  };

  const toggleDesktopAboutUs = () => {
    setDesktopAboutUsOpen(!desktopAboutUsOpen);
    setDesktopSolutionsOpen(false); // Close other desktop dropdown
  };

  // Mobile dropdown toggles
  const toggleMobileSolutions = () => {
    setMobileSolutionsOpen(!mobileSolutionsOpen);
  };

  const toggleMobileAboutUs = () => {
    setMobileAboutUsOpen(!mobileAboutUsOpen);
  };

  // Handle escape key, focus trapping, scroll locking, and cleanup
  useEffect(() => {
    if (mobileMenuOpen) {
      // Lock scroll when menu is open
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
      // Focus trap for menu navigation - defined inside useEffect to avoid stale closure
      const handleKeyDown = (e: KeyboardEvent) => {
        const focusableElements = menuContainerRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );

        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;

        if (e.key === "Tab") {
          if (e.shiftKey) {
            // Shift + Tab: moving backwards
            if (document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            }
          } else {
            // Tab: moving forwards
            if (document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          const currentIndex = Array.from(focusableElements).indexOf(
            document.activeElement as HTMLElement,
          );
          const nextIndex =
            currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
          (focusableElements[nextIndex] as HTMLElement).focus();
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          const currentIndex = Array.from(focusableElements).indexOf(
            document.activeElement as HTMLElement,
          );
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
          (focusableElements[prevIndex] as HTMLElement).focus();
        }
      };

      // Add escape key and keyboard navigation listeners
      const handleEscapeKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          closeMenuAndDropdowns();
        }
      };

      // Click outside handler for additional robustness
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        const hamburgerButton = hamburgerButtonRef.current;
        const menuContainer = menuContainerRef.current;

        // Don't close if clicking on hamburger button (it has its own toggle logic)
        // Don't close if clicking inside the menu container
        if (
          hamburgerButton?.contains(target) ||
          menuContainer?.contains(target)
        ) {
          return;
        }

        // Close menu and dropdowns if clicking outside
        closeMenuAndDropdowns();
      };

      document.addEventListener("keydown", handleEscapeKey);
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("keydown", handleEscapeKey);
        document.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("mousedown", handleClickOutside);
        // Unlock scroll when menu closes
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        // Cleanup event listeners
      };
    } else {
      // Unlock scroll when menu closes
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      // Reset animation state when menu is closed
      setIsAnimating(false);
      // Only close dropdowns if not navigating
      const state = window.history.state;
      if (!state?.usr?.keepMenuOpen) {
        setDesktopSolutionsOpen(false);
        setDesktopAboutUsOpen(false);
      }
    }
  }, [mobileMenuOpen]);

  // Desktop dropdown click-outside handler
  useEffect(() => {
    const handleDesktopClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Only handle desktop dropdowns (not mobile)
      if (window.innerWidth < 769) return;

      // Close Solutions dropdown if clicking outside
      if (
        desktopSolutionsOpen &&
        solutionsDesktopRef.current &&
        !solutionsDesktopRef.current.contains(target)
      ) {
        setDesktopSolutionsOpen(false);
      }

      // Close About Us dropdown if clicking outside
      if (
        desktopAboutUsOpen &&
        aboutUsDesktopRef.current &&
        !aboutUsDesktopRef.current.contains(target)
      ) {
        setDesktopAboutUsOpen(false);
      }
    };

    // Only add listener if either desktop dropdown is open
    if (desktopSolutionsOpen || desktopAboutUsOpen) {
      document.addEventListener("mousedown", handleDesktopClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleDesktopClickOutside);
    };
  }, [desktopSolutionsOpen, desktopAboutUsOpen]);

  return (
    <div className="sticky top-0 z-30">
      <style>{`
        @media (min-width: 769px) {
          .mobile-layout-769 {
            display: none !important;
          }
          .desktop-layout-769 {
            display: flex !important;
          }
        }
        @media (max-width: 767px) {
          .desktop-layout-767 {
            display: none !important;
          }
        }
      `}</style>
      <header
        className="w-full py-4 lg:py-4 px-6 sm:pl-6 sm:px-4 lg:pl-12 lg:px-8 shadow-sm border-b border-gray-100"
        style={{
          backgroundColor: BRAND_COLORS.clinical.white,
          color: BRAND_COLORS.innovation.electricBlue,
        }}
      >
        {/* Mobile Layout - Logo left, hamburger right */}
        <div className="mobile-layout-769 flex items-center justify-between w-full">
          <Link
            to="/"
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img
              src={LOGOS.header}
              alt="Axentra Logo"
              className="h-[4rem]"
              width={LOGO_DIMENSIONS.header.width}
              height={LOGO_DIMENSIONS.header.height}
              loading="eager"
              fetchPriority="high"
              decoding="sync"
            />
          </Link>

          {/* Mobile Hamburger - Visible on mobile only */}
          <button
            ref={hamburgerButtonRef}
            className="focus:outline-none p-2 rounded-lg transition-colors"
            style={{
              color: BRAND_COLORS.innovation.electricBlue,
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                WEBSITE_COLORS.electricBlue[100])
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="navigation-menu"
          >
            <div className="w-6 h-6 relative flex flex-col justify-center items-center">
              <span
                className={`block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out absolute ${
                  iconOpen ? "rotate-45" : "-translate-y-1.5"
                }`}
              ></span>
              <span
                className={`block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${
                  iconOpen ? "opacity-0" : "opacity-100"
                }`}
              ></span>
              <span
                className={`block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out absolute ${
                  iconOpen ? "-rotate-45" : "translate-y-1.5"
                }`}
              ></span>
            </div>
          </button>
        </div>

        {/* Desktop Layout - Logo left, centered navigation links, Contact Us CTA right */}
        <div className="desktop-layout-769 hidden items-center justify-between w-full">
          {/* Left side - Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <img
                src={LOGOS.header}
                alt="Axentra Logo"
                className="h-[3rem] sm:h-[4rem] lg:h-[5rem]"
                width={LOGO_DIMENSIONS.header.width}
                height={LOGO_DIMENSIONS.header.height}
                loading="eager"
                fetchPriority="high"
                decoding="sync"
              />
            </Link>
          </div>

          {/* Center - Navigation Links */}
          <nav className="flex items-center space-x-1 lg:space-x-4">
            {/* Solutions Dropdown */}
            <div className="relative" ref={solutionsDesktopRef}>
              <button
                className="px-2 py-1.5 text-xs lg:px-3 lg:py-2 lg:text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#cce5ff] flex items-center"
                style={{ color: BRAND_COLORS.innovation.electricBlue }}
                onClick={toggleDesktopSolutions}
                aria-expanded={desktopSolutionsOpen}
              >
                Solutions
                <svg
                  className={`w-4 h-4 ml-1 transition-transform duration-200 ${desktopSolutionsOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {desktopSolutionsOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {solutionsSubItems.map((item) => (
                    <Link
                      key={item.id}
                      to={item.to}
                      className="block px-4 py-2 text-sm hover:bg-[#cce5ff] transition-colors duration-200"
                      style={{ color: BRAND_COLORS.innovation.electricBlue }}
                      onClick={() => setDesktopSolutionsOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Pricing Link */}
            <Link
              to="/pricing"
              className="px-2 py-1.5 text-xs lg:px-3 lg:py-2 lg:text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#cce5ff]"
              style={{ color: BRAND_COLORS.innovation.electricBlue }}
            >
              Pricing
            </Link>

            {/* Blog Link */}
            <Link
              to="/blog"
              className="px-2 py-1.5 text-xs lg:px-3 lg:py-2 lg:text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#cce5ff]"
              style={{ color: BRAND_COLORS.innovation.electricBlue }}
            >
              Blog
            </Link>

            {/* Safety Link */}
            <Link
              to="/safety"
              className="px-2 py-1.5 text-xs lg:px-3 lg:py-2 lg:text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#cce5ff]"
              style={{ color: BRAND_COLORS.innovation.electricBlue }}
            >
              Safety
            </Link>

            {/* About Us Dropdown */}
            <div className="relative" ref={aboutUsDesktopRef}>
              <button
                className="px-2 py-1.5 text-xs lg:px-3 lg:py-2 lg:text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#cce5ff] flex items-center"
                style={{ color: BRAND_COLORS.innovation.electricBlue }}
                onClick={toggleDesktopAboutUs}
                aria-expanded={desktopAboutUsOpen}
              >
                About
                <svg
                  className={`w-4 h-4 ml-1 transition-transform duration-200 ${desktopAboutUsOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {desktopAboutUsOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {aboutUsSubItems.map((item) => (
                    <Link
                      key={item.id}
                      to={item.to}
                      className="block px-4 py-2 text-sm hover:bg-[#cce5ff] transition-colors duration-200"
                      style={{ color: BRAND_COLORS.innovation.electricBlue }}
                      onClick={() => setDesktopAboutUsOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Careers Link */}
            <Link
              to="/careers"
              className="px-2 py-1.5 text-xs lg:px-3 lg:py-2 lg:text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#cce5ff]"
              style={{ color: BRAND_COLORS.innovation.electricBlue }}
            >
              Careers
            </Link>
          </nav>

          {/* Right side - Contact Us CTA */}
          <div className="flex items-center">
            <Link
              to="/contact"
              className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 text-white"
              style={{ backgroundColor: BRAND_COLORS.innovation.electricBlue }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#004080")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor =
                  BRAND_COLORS.innovation.electricBlue)
              }
            >
              Contact Us
            </Link>
          </div>
        </div>
      </header>

      {/* Navigation Dropdown - Mobile Only */}
      {mobileMenuOpen && (
        <>
          {/* Dropdown container - Mobile only */}
          <div
            ref={menuContainerRef}
            id="navigation-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Main navigation menu"
            className={`mobile-layout-769 fixed inset-0 z-50 transform transition-all duration-300 ease-out ${
              isAnimating
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-2"
            }`}
            style={{ backgroundColor: BRAND_COLORS.clinical.white }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header bar with logo and close button */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <Link
                to="/"
                className="flex items-center hover:opacity-80 transition-opacity"
                onClick={toggleMobileMenu}
              >
                <img
                  src={LOGOS.header}
                  alt="Axentra Logo"
                  className="h-[5rem]"
                  width={LOGO_DIMENSIONS.header.width}
                  height={LOGO_DIMENSIONS.header.height}
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                />
              </Link>

              <button
                className="p-2 rounded-lg transition-colors"
                style={{
                  color: BRAND_COLORS.innovation.electricBlue,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    WEBSITE_COLORS.electricBlue[100])
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
                onClick={toggleMobileMenu}
                aria-label="Close navigation menu"
              >
                <div className="w-6 h-6 relative flex flex-col justify-center items-center">
                  <span className="block h-0.5 w-6 bg-current transform rotate-45 absolute"></span>
                  <span className="block h-0.5 w-6 bg-current transform -rotate-45 absolute"></span>
                </div>
              </button>
            </div>

            <nav
              className="flex-1 overflow-y-auto px-4 py-4 pb-8"
              style={{
                color: BRAND_COLORS.innovation.electricBlue,
              }}
            >
              <div className="space-y-2 lg:space-y-1 transform transition-all duration-500 ease-out lg:transition-opacity lg:duration-300">
                {/* Solutions Dropdown */}
                <div className="relative">
                  <button
                    className="group flex items-center w-full px-4 py-3 hover:bg-[#cce5ff] rounded-lg transition-all duration-200 lg:px-3 lg:py-3"
                    onClick={toggleMobileSolutions}
                    aria-expanded={mobileSolutionsOpen}
                    aria-controls="solutions-submenu"
                    aria-label="Solutions submenu"
                  >
                    <div className="flex items-center justify-center w-10 h-10 bg-[#cce5ff] rounded-lg mr-4 group-hover:bg-[#99ccff] transition-colors lg:w-8 lg:h-8 lg:mr-3">
                      <svg
                        className="w-5 h-5 lg:w-4 lg:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11H5m14-7H5m14 14H5"
                        />
                      </svg>
                    </div>
                    <span className="text-lg font-medium lg:text-base flex-1 text-left">
                      Our Solutions
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${mobileSolutionsOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {mobileSolutionsOpen && (
                    <div
                      id="solutions-submenu"
                      className="ml-6 mt-2 space-y-1"
                      role="menu"
                      aria-label="Solutions submenu"
                    >
                      {solutionsSubItems.map((item) => (
                        <SubNavigationLink key={item.id} item={item} />
                      ))}
                    </div>
                  )}
                </div>

                {/* About Us Dropdown */}
                <div className="relative">
                  <button
                    className="group flex items-center w-full px-4 py-3 hover:bg-[#cce5ff] rounded-lg transition-all duration-200 lg:px-3 lg:py-3"
                    onClick={toggleMobileAboutUs}
                    aria-expanded={mobileAboutUsOpen}
                    aria-controls="aboutus-submenu"
                    aria-label="About Us submenu"
                  >
                    <div className="flex items-center justify-center w-10 h-10 bg-[#cce5ff] rounded-lg mr-4 group-hover:bg-[#99ccff] transition-colors lg:w-8 lg:h-8 lg:mr-3">
                      <svg
                        className="w-5 h-5 lg:w-4 lg:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <span className="text-lg font-medium lg:text-base flex-1 text-left">
                      About Us
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${mobileAboutUsOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {mobileAboutUsOpen && (
                    <div
                      id="aboutus-submenu"
                      className="ml-6 mt-2 space-y-1"
                      role="menu"
                      aria-label="About Us submenu"
                    >
                      {aboutUsSubItems.map((item) => (
                        <SubNavigationLink key={item.id} item={item} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Other Navigation Links (excluding About, News, Blog, Contact) */}
                {navigationItems
                  .slice(1)
                  .filter(
                    (item) =>
                      !["solutions", "about", "news", "contact"].includes(
                        item.id,
                      ),
                  )
                  .map((item) => (
                    <Link
                      key={item.id}
                      to={item.to}
                      className="group flex items-center px-4 py-3 hover:bg-[#cce5ff] rounded-lg transition-all duration-200 lg:px-3 lg:py-3"
                      onClick={toggleMobileMenu}
                    >
                      <div className="flex items-center justify-center w-10 h-10 bg-[#cce5ff] rounded-lg mr-4 group-hover:bg-[#99ccff] transition-colors lg:w-8 lg:h-8 lg:mr-3">
                        <svg
                          className="w-5 h-5 lg:w-4 lg:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={item.icon}
                          />
                        </svg>
                      </div>
                      <span className="text-lg font-medium lg:text-base">
                        {item.label}
                      </span>
                    </Link>
                  ))}

                {/* Contact Us - Highlighted as CTA */}
                <Link
                  to="/contact"
                  className="group flex items-center px-4 py-3 rounded-lg transition-all duration-200 text-white mt-4"
                  style={{
                    backgroundColor: BRAND_COLORS.innovation.electricBlue,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#004080")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      BRAND_COLORS.innovation.electricBlue)
                  }
                  onClick={toggleMobileMenu}
                >
                  <div className="flex items-center justify-center w-10 h-10 bg-white/20 rounded-lg mr-4 group-hover:bg-white/30 transition-colors lg:w-8 lg:h-8 lg:mr-3">
                    <svg
                      className="w-5 h-5 lg:w-4 lg:h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <span className="text-lg font-medium lg:text-base text-white">
                    Contact Us
                  </span>
                </Link>
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
